import { computeExportVideoBitrateBps } from '@/rendering/export/adapters/webCodecsExportBitrate'
import type { ExportRenderQualityId } from '@/rendering/export/types'

export type PickedWebCodecsH264Config = {
  codec: string
  bitrate: number
  framerate: number
  width: number
  height: number
}

type PickSupportedH264ConfigOptions = {
  width: number
  height: number
  framerate?: number
  bitrate?: number
}

const H264_CODEC_CANDIDATES: readonly string[] = [
  'avc1.640028',
  'avc1.640029',
  'avc1.64002A',
  'avc1.640032',
  'avc1.4D4028',
  'avc1.42E028',
]

function logVideoExport(message: string, extra?: Record<string, unknown>): void {
  if (!import.meta.env.DEV) return
  if (extra) {
    console.info(`[VideoExport] ${message}`, extra)
    return
  }
  console.info(`[VideoExport] ${message}`)
}

function clampFrameRate(framerate: number | undefined): number {
  const fallback = 30
  return Math.min(60, Math.max(1, Math.round(framerate ?? fallback)))
}

function clampMp4Bitrate(
  width: number,
  height: number,
  bitrate: number | undefined,
): number {
  const fallback = computeExportVideoBitrateBps(width, height, 'high')
  const base = Math.round(bitrate ?? fallback)
  const max2kBps = 16_000_000
  const min2kBps = 10_000_000
  const is2kOrAbove = width >= 2048 || height >= 1440
  if (!is2kOrAbove) return Math.max(1_000_000, Math.min(20_000_000, base))
  return Math.max(min2kBps, Math.min(max2kBps, base))
}

export async function pickSupportedH264Config(
  options: PickSupportedH264ConfigOptions,
): Promise<VideoEncoderConfig | null> {
  if (typeof VideoEncoder === 'undefined') return null

  const width = Math.max(1, Math.round(options.width))
  const height = Math.max(1, Math.round(options.height))
  const framerate = clampFrameRate(options.framerate)
  const bitrate = clampMp4Bitrate(width, height, options.bitrate)

  for (const codec of H264_CODEC_CANDIDATES) {
    const config: VideoEncoderConfig = {
      codec,
      width,
      height,
      bitrate,
      framerate,
      hardwareAcceleration: 'prefer-hardware',
      bitrateMode: 'variable',
      latencyMode: 'quality',
      avc: { format: 'avc' },
    }

    try {
      const result = await VideoEncoder.isConfigSupported(config)
      logVideoExport('H.264 config probe', {
        codec,
        width,
        height,
        framerate,
        bitrate,
        supported: result.supported,
        resolvedCodec: result.config?.codec ?? null,
      })
      if (result.supported) {
        return result.config ?? config
      }
    } catch (error) {
      logVideoExport('H.264 config probe threw', {
        codec,
        width,
        height,
        framerate,
        bitrate,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return null
}

export async function pickWebCodecsH264Config(
  width: number,
  height: number,
  fps: number,
  quality: ExportRenderQualityId,
): Promise<PickedWebCodecsH264Config | null> {
  if (typeof VideoEncoder === 'undefined') return null

  const normalizedWidth = Math.max(1, Math.round(width))
  const normalizedHeight = Math.max(1, Math.round(height))
  const requestedBitrate = computeExportVideoBitrateBps(normalizedWidth, normalizedHeight, quality)
  const requestedFps = clampFrameRate(fps)
  const probeFpsList =
    normalizedWidth >= 2048 || normalizedHeight >= 1440
      ? Array.from(new Set([Math.min(requestedFps, 30), 30, requestedFps]))
      : [requestedFps]

  for (const framerate of probeFpsList) {
    const config = await pickSupportedH264Config({
      width: normalizedWidth,
      height: normalizedHeight,
      framerate,
      bitrate: requestedBitrate,
    })
    if (!config?.codec) continue

    const codec = config.codec
    const bitrate = clampMp4Bitrate(
      normalizedWidth,
      normalizedHeight,
      typeof config.bitrate === 'number' ? config.bitrate : requestedBitrate,
    )
    logVideoExport('Selected H.264 config', {
      codec,
      width: normalizedWidth,
      height: normalizedHeight,
      framerate,
      bitrate,
    })
    return {
      codec,
      bitrate,
      framerate,
      width: normalizedWidth,
      height: normalizedHeight,
    }
  }

  logVideoExport('No supported H.264 config found', {
    width: normalizedWidth,
    height: normalizedHeight,
    fps: requestedFps,
    quality,
  })
  return null
}
